import { useState, useEffect, useCallback } from 'react';
import { useTransactions } from '../context/TransactionContext';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { toast } from 'sonner';
import { Check, ChevronsUpDown, Plus, User, Building2 } from 'lucide-react';
import { cn } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODES_PAIEMENT = [
  { value: 'Chèque', label: 'Chèque' },
  { value: 'Virement', label: 'Virement' },
  { value: 'Espèces', label: 'Espèces' },
  { value: 'CB', label: 'CB' },
  { value: 'PayPal', label: 'PayPal' }
];

const RecetteModal = ({ open, onClose, onSuccess, editData, defaultMonth, defaultYear }) => {
  const { createRecette, updateRecette } = useTransactions();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // CRM integration
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientData, setNewClientData] = useState({ nom: '', type_client: 'particulier' });
  const [creatingClient, setCreatingClient] = useState(false);
  
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    date: getTodayDate(),
    reference: '',
    client: '',
    client_id: null,
    nature: '',
    montant: '',
    mode_encaissement: 'Virement'
  });

  // Fetch CRM clients
  const fetchClients = useCallback(async () => {
    if (!token) return;
    setClientsLoading(true);
    try {
      const response = await axios.get(API_URL + '/api/crm/clients?type_client=particulier', {
        headers: { Authorization: 'Bearer ' + token }
      });
      const particuliers = response.data.clients || [];
      
      const response2 = await axios.get(API_URL + '/api/crm/clients?type_client=entreprise', {
        headers: { Authorization: 'Bearer ' + token }
      });
      const entreprises = response2.data.clients || [];
      
      setClients([...particuliers, ...entreprises]);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setClientsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) {
      fetchClients();
    }
  }, [open, fetchClients]);

  useEffect(() => {
    if (editData) {
      setFormData({
        date: editData.date,
        reference: editData.reference,
        client: editData.client,
        client_id: editData.client_id || null,
        nature: editData.nature,
        montant: String(editData.montant),
        mode_encaissement: editData.mode_encaissement
      });
    } else if (open) {
      setFormData({
        date: getTodayDate(),
        reference: '',
        client: '',
        client_id: null,
        nature: '',
        montant: '',
        mode_encaissement: 'Virement'
      });
    }
    setErrors({});
    setShowNewClientForm(false);
  }, [editData, open]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.date) {
      newErrors.date = 'La date est obligatoire';
    }
    if (!formData.reference.trim()) {
      newErrors.reference = 'La référence est obligatoire';
    }
    if (!formData.client.trim()) {
      newErrors.client = 'Le client est obligatoire';
    }
    if (!formData.nature.trim()) {
      newErrors.nature = 'La nature est obligatoire';
    }
    if (!formData.montant) {
      newErrors.montant = 'Le montant est obligatoire';
    } else {
      const montant = parseFloat(formData.montant);
      if (isNaN(montant) || montant <= 0) {
        newErrors.montant = 'Le montant doit être supérieur à 0';
      }
    }
    if (!formData.mode_encaissement) {
      newErrors.mode_encaissement = 'Le mode de paiement est obligatoire';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSelectClient = (client) => {
    setFormData({ 
      ...formData, 
      client: client.nom,
      client_id: client.id
    });
    setClientPopoverOpen(false);
  };

  const handleCreateClient = async () => {
    if (!newClientData.nom.trim()) {
      toast.error('Le nom du client est requis');
      return;
    }

    setCreatingClient(true);
    try {
      const response = await axios.post(API_URL + '/api/crm/clients', newClientData, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      const newClient = response.data;
      setClients([...clients, newClient]);
      setFormData({ 
        ...formData, 
        client: newClient.nom,
        client_id: newClient.id
      });
      setShowNewClientForm(false);
      setNewClientData({ nom: '', type_client: 'particulier' });
      setClientPopoverOpen(false);
      toast.success('Client créé et sélectionné');
    } catch (error) {
      toast.error('Erreur lors de la création du client');
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const data = {
        ...formData,
        montant: parseFloat(formData.montant)
      };

      if (editData) {
        await updateRecette(editData.id, data);
        toast.success('✓ Recette modifiée');
      } else {
        await createRecette(data);
        toast.success('✓ Recette enregistrée');
      }
      
      onSuccess();
    } catch (error) {
      const message = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="recette-modal">
        <DialogHeader>
          <DialogTitle className="font-chivo text-xl">
            {editData ? 'Modifier la recette' : 'Nouvelle recette'}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {editData ? 'Modifiez les informations de la recette' : 'Ajoutez une nouvelle recette à votre livre de comptes'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date" className="text-sm font-medium">
                Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className={errors.date ? 'border-red-500' : ''}
                data-testid="recette-date-input"
              />
              {errors.date && <p className="text-xs text-red-500">{errors.date}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference" className="text-sm font-medium">
                Référence <span className="text-red-500">*</span>
              </Label>
              <Input
                id="reference"
                type="text"
                placeholder="F0001"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                className={errors.reference ? 'border-red-500' : ''}
                data-testid="recette-reference-input"
              />
              {errors.reference && <p className="text-xs text-red-500">{errors.reference}</p>}
            </div>
          </div>

          {/* Client avec CRM */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Client <span className="text-red-500">*</span>
            </Label>
            <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={clientPopoverOpen}
                  className={cn(
                    "w-full justify-between font-normal",
                    errors.client ? 'border-red-500' : '',
                    !formData.client && "text-muted-foreground"
                  )}
                  data-testid="recette-client-select"
                >
                  {formData.client || "Sélectionner ou créer un client..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                {!showNewClientForm ? (
                  <Command>
                    <CommandInput placeholder="Rechercher un client..." />
                    <CommandList>
                      <CommandEmpty>
                        <p className="text-sm text-muted-foreground py-2">Aucun client trouvé</p>
                      </CommandEmpty>
                      {filteredClients.length > 0 && (
                        <CommandGroup heading="Clients existants">
                          {filteredClients.map((client) => (
                            <CommandItem
                              key={client.id}
                              value={client.nom}
                              onSelect={() => handleSelectClient(client)}
                              className="cursor-pointer"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.client_id === client.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex items-center gap-2 flex-1">
                                {client.type_client === 'entreprise' ? (
                                  <Building2 className="h-4 w-4 text-blue-500" />
                                ) : (
                                  <User className="h-4 w-4 text-slate-400" />
                                )}
                                <span>{client.nom}</span>
                                <span className="text-xs text-muted-foreground ml-auto">
                                  {client.type_client === 'entreprise' ? 'Entreprise' : 'Particulier'}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                      <CommandSeparator />
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => setShowNewClientForm(true)}
                          className="cursor-pointer text-emerald-600"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Créer un nouveau client
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                ) : (
                  <div className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Nouveau client</h4>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setShowNewClientForm(false)}
                      >
                        Retour
                      </Button>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Nom du client *</Label>
                        <Input
                          placeholder="Nom ou raison sociale"
                          value={newClientData.nom}
                          onChange={(e) => setNewClientData({ ...newClientData, nom: e.target.value })}
                          autoFocus
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Select 
                          value={newClientData.type_client} 
                          onValueChange={(v) => setNewClientData({ ...newClientData, type_client: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="particulier">Particulier</SelectItem>
                            <SelectItem value="entreprise">Entreprise</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        className="w-full" 
                        onClick={handleCreateClient}
                        disabled={creatingClient}
                      >
                        {creatingClient ? 'Création...' : 'Créer et sélectionner'}
                      </Button>
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            {errors.client && <p className="text-xs text-red-500">{errors.client}</p>}
            {formData.client && !formData.client_id && (
              <p className="text-xs text-amber-600">
                💡 Ce client n&apos;est pas dans votre CRM. Créez-le pour un meilleur suivi.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="nature" className="text-sm font-medium">
              Nature / Prestation <span className="text-red-500">*</span>
            </Label>
            <Input
              id="nature"
              type="text"
              placeholder="Prestation de service, vente produit..."
              value={formData.nature}
              onChange={(e) => setFormData({ ...formData, nature: e.target.value })}
              className={errors.nature ? 'border-red-500' : ''}
              data-testid="recette-nature-input"
            />
            {errors.nature && <p className="text-xs text-red-500">{errors.nature}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="montant" className="text-sm font-medium">
                Montant (€) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="montant"
                type="number"
                step="0.01"
                min="0"
                placeholder="1250.00"
                value={formData.montant}
                onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                className={errors.montant ? 'border-red-500' : ''}
                data-testid="recette-montant-input"
              />
              {errors.montant && <p className="text-xs text-red-500">{errors.montant}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mode" className="text-sm font-medium">
                Mode de paiement <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.mode_encaissement} 
                onValueChange={(value) => setFormData({ ...formData, mode_encaissement: value })}
              >
                <SelectTrigger 
                  className={errors.mode_encaissement ? 'border-red-500' : ''}
                  data-testid="recette-mode-select"
                >
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {MODES_PAIEMENT.map((mode) => (
                    <SelectItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.mode_encaissement && <p className="text-xs text-red-500">{errors.mode_encaissement}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="recette-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
              data-testid="recette-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Enregistrement...
                </span>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RecetteModal;
